import { useEffect, useState } from 'react';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { BestInDentalV2 } from '~/constants/ff.posthog';
import { useBidHistoryQuery } from './useGetBidHistory';

export interface IsBestInDentalVisibleResult {
  visible: boolean;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Access gate (GOTU-4820): PostHog flag ON and ≥1 BID membership for the
 * selected office. Shares the page-1 query cache entry with the BID page
 * itself, so navigating to the page never double-fetches.
 *
 * Gates on `totalCount`, not the row array (GOTU-5630): an out-of-range page
 * returns `cycles: []` with a non-zero `totalCount`, and a member with no
 * memberships gets 200 + `totalCount: 0` (never null, never 403).
 *
 * `isLoading` surfaces the in-flight state so route guards can defer their
 * redirect decision until the gate has actually resolved (GOTU-5641 final
 * review): consumers must not treat `visible: false` as authoritative while
 * `isLoading` is true, or direct URL access will be redirected before the
 * flag/query settle.
 */
export const useIsBestInDentalVisible = (): IsBestInDentalVisibleResult => {
  const isFlagOn = useFeatureFlag(BestInDentalV2);
  const query = useBidHistoryQuery(1, isFlagOn);

  // PostHog resolves flags from localStorage cache synchronously on repeat
  // visits, but the first cold-session render returns `false` before the
  // /decide response arrives.  `useFeatureFlag` subscribes to onFeatureFlags
  // and will re-render when the async resolution completes.  We defer the
  // "flag is off" decision by one tick so that synchronous cache hits are
  // already reflected; anything still pending after mount relies on the
  // subscription to flip the flag (and trigger a re-render).
  const [hasMounted, setHasMounted] = useState(false);
  useEffect(() => {
    setHasMounted(true);
  }, []);

  const isFlagLoading = !isFlagOn && !hasMounted;

  return {
    visible: isFlagOn && (query.data?.totalCount ?? 0) > 0,
    isLoading: (isFlagOn && query.isLoading) || isFlagLoading,
    isError: isFlagOn && query.isError,
  };
};
